/**
 * AIDashboard Component
 *
 * Right pane showing AI-prioritized email insights.
 */

import React from 'react';
import { useTranslation } from 'react-i18next';
import type { AIDashboardResponse, AIDashboardThread } from '@/services/emailApi';
import { formatEmailDate } from '@/utils/dateFormat';

// Priority color mapping
const PRIORITY_COLORS: Record<number, { bg: string; text: string; border: string }> = {
  5: { bg: 'bg-red-100', text: 'text-red-700', border: 'border-red-300' },
  4: { bg: 'bg-orange-100', text: 'text-orange-700', border: 'border-orange-300' },
  3: { bg: 'bg-yellow-100', text: 'text-yellow-700', border: 'border-yellow-300' },
  2: { bg: 'bg-blue-100', text: 'text-blue-700', border: 'border-blue-300' },
  1: { bg: 'bg-gray-100', text: 'text-gray-600', border: 'border-gray-300' },
};

// Category color mapping
const CATEGORY_COLORS: Record<string, { bg: string; text: string }> = {
  urgent: { bg: 'bg-red-100', text: 'text-red-700' },
  action_required: { bg: 'bg-orange-100', text: 'text-orange-700' },
  question: { bg: 'bg-blue-100', text: 'text-blue-700' },
  fyi: { bg: 'bg-gray-100', text: 'text-gray-600' },
  meeting: { bg: 'bg-purple-100', text: 'text-purple-700' },
  follow_up: { bg: 'bg-amber-100', text: 'text-amber-700' },
  approval: { bg: 'bg-green-100', text: 'text-green-700' },
  notification: { bg: 'bg-cyan-100', text: 'text-cyan-700' },
  default: { bg: 'bg-purple-100', text: 'text-purple-700' },
};

interface SummaryCardProps {
  thread: AIDashboardThread;
  onSelect: (threadId: string) => void;
  isUrgent?: boolean;
}

const SummaryCard: React.FC<SummaryCardProps> = ({ thread, onSelect, isUrgent }) => {
  const { t } = useTranslation('email');
  const priority = thread.ai_summary?.priority_score || 3;
  const priorityColors = PRIORITY_COLORS[Math.min(5, Math.max(1, priority))];
  const categoryKey = (thread.ai_summary?.category || 'default').toLowerCase().replace(/\s+/g, '_');
  const categoryColors = CATEGORY_COLORS[categoryKey] || CATEGORY_COLORS.default;

  return (
    <div
      onClick={() => onSelect(thread.id)}
      className={`
        bg-white rounded-lg border p-3 cursor-pointer transition-all hover:shadow-md
        ${isUrgent ? 'border-red-300 animate-pulse hover:animate-none' : 'border-gray-200'}
        ${thread.unread_count > 0 ? 'border-l-4 border-l-blue-500' : ''}
      `}
    >
      <div className="flex items-start justify-between gap-2 mb-2">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <span className={`text-sm font-semibold truncate ${thread.unread_count > 0 ? 'text-gray-900' : 'text-gray-700'}`}>
              {thread.sender_name || thread.sender_email}
            </span>
            {thread.message_count > 1 && (
              <span className="text-xs text-gray-400 bg-gray-100 px-1.5 rounded flex-shrink-0">
                {thread.message_count}
              </span>
            )}
          </div>
          <p className={`text-sm truncate ${thread.unread_count > 0 ? 'font-medium text-gray-900' : 'text-gray-600'}`}>
            {thread.subject || t('common.no_subject')}
          </p>
        </div>
        <span className="text-xs text-gray-400 whitespace-nowrap flex-shrink-0">
          {thread.last_message_at ? formatEmailDate(thread.last_message_at) : ''}
        </span>
      </div>

      {/* AI Summary */}
      {thread.ai_summary && (
        <>
          {thread.ai_summary.one_liner && (
            <p className="text-xs text-gray-700 mb-2 line-clamp-2">
              {thread.ai_summary.one_liner}
            </p>
          )}

          <div className="flex items-center gap-1.5 flex-wrap">
            {/* Priority badge */}
            {priority >= 4 && (
              <span className={`inline-flex items-center gap-0.5 text-[10px] px-1.5 py-0.5 rounded ${priorityColors.bg} ${priorityColors.text} font-medium`}>
                <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                </svg>
                {priority === 5 ? t('ai.urgent') : t('ai.high')}
              </span>
            )}

            {/* Category */}
            <span className={`text-[10px] px-1.5 py-0.5 rounded ${categoryColors.bg} ${categoryColors.text}`}>
              {thread.ai_summary.category || 'Email'}
            </span>

            {/* Response needed */}
            {thread.ai_summary.requires_response && (
              <span className="inline-flex items-center gap-0.5 text-[10px] px-1.5 py-0.5 bg-amber-100 text-amber-700 rounded">
                <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h10a8 8 0 018 8v2M3 10l6 6m-6-6l6-6" />
                </svg>
                {t('compose.reply')}
              </span>
            )}

            {/* Sentiment */}
            {thread.ai_summary.sentiment && thread.ai_summary.sentiment !== 'neutral' && (
              <span className={`text-[10px] px-1.5 py-0.5 rounded ${
                thread.ai_summary.sentiment === 'positive' ? 'bg-green-100 text-green-700' :
                thread.ai_summary.sentiment === 'negative' ? 'bg-red-100 text-red-700' :
                'bg-gray-100 text-gray-600'
              }`}>
                {thread.ai_summary.sentiment === 'positive' ? '😊' : '😟'} {thread.ai_summary.sentiment}
              </span>
            )}

            {/* Attachments */}
            {thread.has_attachments && (
              <span className="text-[10px] px-1.5 py-0.5 bg-gray-100 text-gray-600 rounded flex items-center gap-0.5">
                <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" />
                </svg>
                {t('compose.files')}
              </span>
            )}
          </div>
        </>
      )}
    </div>
  );
};

interface AIDashboardProps {
  aiDashboard: AIDashboardResponse | null;
  hasAIDashboard: boolean;
  loadingDashboard: boolean;
  onSelectThread: (threadId: string) => void;
}

const AIDashboard: React.FC<AIDashboardProps> = ({
  aiDashboard,
  hasAIDashboard,
  loadingDashboard,
  onSelectThread,
}) => {
  const { t } = useTranslation('email');
  if (loadingDashboard) {
    return (
      <div className="flex-1 hidden md:flex items-center justify-center">
        <div className="animate-spin h-8 w-8 border-4 border-purple-600 border-t-transparent rounded-full" />
      </div>
    );
  }

  if (!hasAIDashboard || !aiDashboard) {
    return (
      <div className="flex-1 hidden md:flex items-center justify-center text-gray-400">
        <div className="text-center">
          <svg className="w-24 h-24 mx-auto mb-4 opacity-50" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
          </svg>
          <p className="text-lg font-medium">{t('dashboard.select_email_to_read')}</p>
          <p className="text-sm text-gray-400 mt-1">{t('dashboard.choose_message')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex-1 hidden md:flex flex-col overflow-y-auto bg-gray-50 p-4">
      <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
        <svg className="w-5 h-5 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
        </svg>
        {t('ai.ai_insights')}
        <span className="text-xs font-normal text-gray-500">
          ({t('ai.emails_analyzed', { count: aiDashboard.stats.total_with_ai })})
        </span>
      </h2>

      {/* Urgent - with blinking animation */}
      {aiDashboard.urgent.length > 0 && (
        <div className="mb-6">
          <h3 className="text-sm font-semibold text-red-700 mb-2 flex items-center gap-2">
            <span className="relative flex h-3 w-3">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75"></span>
              <span className="relative inline-flex rounded-full h-3 w-3 bg-red-500"></span>
            </span>
            {t('dashboard.urgent')} ({aiDashboard.urgent.length})
          </h3>
          <div className="space-y-2">
            {aiDashboard.urgent.map(thread => (
              <SummaryCard key={thread.id} thread={thread} onSelect={onSelectThread} isUrgent />
            ))}
          </div>
        </div>
      )}

      {/* Unread with Action Items */}
      {aiDashboard.unread_with_action.length > 0 && (
        <div className="mb-6">
          <h3 className="text-sm font-semibold text-orange-700 mb-2 flex items-center gap-2">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
            {t('ai.needs_attention')} ({aiDashboard.unread_with_action.length})
          </h3>
          <div className="space-y-2">
            {aiDashboard.unread_with_action.map(thread => (
              <SummaryCard key={thread.id} thread={thread} onSelect={onSelectThread} />
            ))}
          </div>
        </div>
      )}

      {/* Other Unread */}
      {aiDashboard.unread.length > 0 && (
        <div className="mb-6">
          <h3 className="text-sm font-semibold text-blue-700 mb-2 flex items-center gap-2">
            <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
              <circle cx="10" cy="10" r="5" />
            </svg>
            {t('filters.unread')} ({aiDashboard.unread.length})
          </h3>
          <div className="space-y-2">
            {aiDashboard.unread.map(thread => (
              <SummaryCard key={thread.id} thread={thread} onSelect={onSelectThread} />
            ))}
          </div>
        </div>
      )}

      {/* Read but has Action Items */}
      {aiDashboard.read_with_action.length > 0 && (
        <div className="mb-6">
          <h3 className="text-sm font-semibold text-gray-600 mb-2 flex items-center gap-2">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
            </svg>
            {t('ai.pending_actions')} ({aiDashboard.read_with_action.length})
          </h3>
          <div className="space-y-2">
            {aiDashboard.read_with_action.map(thread => (
              <SummaryCard key={thread.id} thread={thread} onSelect={onSelectThread} />
            ))}
          </div>
        </div>
      )}

      {/* No items in any group (all caught up) */}
      {aiDashboard.urgent.length === 0 &&
       aiDashboard.unread_with_action.length === 0 &&
       aiDashboard.unread.length === 0 &&
       aiDashboard.read_with_action.length === 0 && (
        <div className="flex-1 flex items-center justify-center text-gray-400">
          <div className="text-center">
            <svg className="w-16 h-16 mx-auto mb-4 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <p className="text-lg font-medium text-gray-600">{t('ai.all_caught_up')}</p>
            <p className="text-sm text-gray-400 mt-1">{t('ai.no_urgent_items')}</p>
          </div>
        </div>
      )}
    </div>
  );
};

export default AIDashboard;
